import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sb } from '../supabase';
import usePageTitle from '../hooks/usePageTitle';
import MarketShell from '../components/market/MarketShell';
import { marketTitle, variantText } from '../lib/marketCatalog';
import { formatSar } from '../lib/marketPricing';
import { clearCart, getCartMethod, getCheckoutSelection, removeLines, setCartMethod } from '../lib/marketCart';
import { useDeliveredConfig } from '../lib/marketMode';
import { arrival, cheaperMethod, DELIVERED_T, productPrice, shortDate } from '../lib/marketDelivered';

import { claimQuote, createOrder, getCheckoutConfig, MARKET_API_IS_LOCAL, requestQuote, startPayment } from '../lib/marketOrdersApi';
import { listAddresses, saveAddress } from '../lib/marketAddresses';
import { clearCheckoutState, loadCheckoutState, saveCheckoutState } from '../lib/checkoutState';
import CheckoutAuth from '../components/market/CheckoutAuth';
import { validateAddress } from '../lib/saAddress';
import { getTurnstileToken, turnstileEnabled } from '../lib/turnstile';
import PreviewBanner from '../components/market/PreviewBanner';
import AddressBook, { AddressSummary } from '../components/market/AddressBook';
import { Row, useCartPricing } from './MarketCart';
import { track } from '../lib/growthTracker';
import { recordOrderAttribution } from '../lib/attribution';
import { getAnonId } from '../lib/claimFunnel';

// The method's own name — shown on this page only, under the chosen option
// (owner, 2026-10-29: «جوي» / «بحري» appear nowhere else).
const METHOD_NAME = {
  ar: { air: 'جوي', sea: 'بحري', fast: 'إكسبريس' },
  en: { air: 'Air', sea: 'Sea', fast: 'Express' },
  zh: { air: '空运', sea: '海运', fast: '快递' },
};

// Checkout — select, cart, delivery, review the total, pay once. Every amount
// after "Check price and shipping" comes from the server (market-checkout):
// the cart is re-priced from the database, the supplier confirms price and
// stock (order preview), shipping is estimated for this parcel, and the
// Service Fee (5% of the supplier value) is computed there. The page computes
// nothing it charges. Shipping is ONE line; its make-up stays internal.
//
// Honest by construction:
//   • goods the supplier could not confirm block with the reason and NO number;
//   • shipping is priced for every supported product: from the supplier's
//     measurements, or — when they are missing — MAABAR's internal estimate
//     (market-checkout shipping.ts), quoted live by the carrier at that weight.
//     The customer sees ONE total, pays once, and is never asked for more for
//     this order; the page does not call it a carrier-confirmed rate. A parcel
//     over the route's 10 kg is refused with what to do. If the freight quote
//     itself fails, the page offers "try again" (the staff-pricing path exists
//     on the server for staff, not as a customer step);
//   • ONE primary action, "Continue checkout" (owner, 2026-10-21): it calculates
//     the full total on the server — for a GUEST too (market_guest_checkout),
//     after Cloudflare Turnstile vouches for the visitor (verified server-side).
//     The buyer reviews the final amount and confirms it ("Confirm amount and
//     continue"); only that confirmation leads on — to payment when signed in,
//     or first to CheckoutAuth (WhatsApp code, Google, Apple, email code; no
//     password) for a guest. After sign-in the server attaches the quote to the
//     account (claim), the address / name / contact phone are saved, and
//     payment follows at the amount the buyer confirmed. Cart, address and
//     quote survive the sign-in, even a Google round trip (checkoutState.js).
//     If the quote expired or changed meanwhile, the new amount is shown and
//     must be confirmed again — nothing is charged at an amount not accepted;
//   • before paying, the buyer reads that fulfilment depends on availability
//     and shipping, with a full refund if MAABAR cannot fulfil — a payment is
//     an order received, not a confirmed purchase from the supplier;
//   • a supplier price that moved since the cart is shown and must be
//     acknowledged before paying;
//   • Saudi import VAT (15% of the declared value) and the customs fee are
//     their own line, paid here once (owner, 2026-10-26) — the total is
//     all-in, and the page says so in one short line.

const T = {
  ar: {
    title: 'إتمام الطلب', back: 'رجوع إلى السلة', preview: 'بيئة اختبار — الدفع تجريبي.',
    review: '١. مراجعة الطلب', delivery: '٢. بيانات التوصيل', services: '٣. خدمات إضافية (اختيارية)', summary: 'الملخص',
    notValid: 'في السلة بنود تحتاج تصحيحًا — ارجع إلى السلة.',
    needAddress: 'اختر عنوان التوصيل أو أكمل العنوان الجديد.', deliverTo: 'التوصيل إلى',
    check: 'أكمل الشراء', checking: 'نؤكد التوفر ونحسب الشحن…', checkSteps: ['1/3 نتأكد من توفر المنتجات عند المورد…', '2/3 نحسب الشحن لعنوانك…', '3/3 نجهّز الإجمالي، لحظات…'], recheck: 'أكمل الشراء',
    goods: 'قيمة البضاعة', shipping: 'الشحن إلى السعودية', servicesLine: 'الخدمات الإضافية',
    fee: 'رسوم الخدمة', vat: 'ضريبة القيمة المضافة', total: 'الإجمالي', importLine: 'ضريبة الاستيراد والتخليص',
    customs: 'شامل ضريبة الاستيراد 15% ورسوم التخليص.',
    validUntil: (t) => `السعر محجوز حتى ${t}.`, expired: 'انتهت صلاحية السعر — أعد التحقق.',
    changed: 'تغيّر سعر المورد لبعض المنتجات منذ أضفتها:', changedLine: (n, a, b) => `${n}: من ${a} إلى ${b} للوحدة`,
    ackChange: 'اطّلعت على تغيّر السعر وأوافق على السعر الجديد.',
    pay: 'تأكيد الطلب والدفع', paying: 'جارٍ إنشاء الطلب…', checkoutOff: 'إتمام الطلب غير مفعّل بعد.',
    payOff: 'الدفع غير مفعّل بعد — يمكنك إنشاء الطلب ودفعه لاحقًا من صفحة الطلب.', placeOnly: 'تأكيد الطلب',
    blocked: 'لا يمكن إتمام هذا الطلب حاليًا:',
    // The owner's words (2026-10-26), the delivery days from the quote.
    fulfilNotice: (days) => `تنفيذ الطلب يعتمد على توفر المنتجات وإمكانية الشحن. وفي حال تعذر التنفيذ، يُعاد لك كامل المبلغ المدفوع${days ? `، ومدة التوصيل المتوقعة: ${days} يوم.` : '.'}`,
    previewNote: 'هذا سعر سلتك. نأكده الحين مع التوفر، وبعدها تدفع.',
    noExtra: 'ملاحظة: لن تُطلب منك أي مبالغ إضافية عند الاستلام.',
    needsShip: 'لم نتمكن من حساب الشحن الآن. حاول مرة أخرى بعد قليل.', retryShip: 'حاول مرة أخرى',
    reason: {
      parcel: 'وزن هذا الطلب يتجاوز حد الشحنة الواحدة إلى السعودية (١٠ كجم). قلّل الكمية أو قسّم المنتجات على طلبين.',
      minimum: (m) => `الحد الأدنى للطلب ${m} — ارجع للسلة وكمّل طلبك.`,
      channel: 'بعض المنتجات غير متاحة للدفع الإلكتروني بعد.',
      supplier: 'لم يؤكد المورد السعر أو التوفر لبعض المنتجات.',
      line: 'بعض البنود تحتاج تصحيحًا في السلة (الكمية أو التوفر).',
      config: 'الدفع الإلكتروني غير متاح حاليًا.',
      busy: 'الخدمة مشغولة — حاول بعد دقيقة.',
    },
    failed: 'تعذّر الإكمال. حاول مرة أخرى.', loading: '…',
    signInToCheck: 'سجّل الدخول لحساب الإجمالي النهائي.', signIn: 'تسجيل الدخول',
    guestLimit: 'طلبات حساب كثيرة من هذا الجهاز — سجّل الدخول للمتابعة أو حاول بعد قليل.', challenge: 'تعذّر التحقق الأمني من الطلب. أعد المحاولة.',
    complete: 'تأكيد المبلغ والمتابعة', reviewLead: 'هذا هو المبلغ النهائي. راجعه ثم أكّد للمتابعة.', challengeDown: 'خدمة التحقق مشغولة الآن. حاول بعد قليل.', notBuyer: 'هذا الحساب ليس حساب مشترٍ، ولا يمكنه الشراء من السوق. استخدم حساب مشترٍ.',
    requoted: 'انتهت صلاحية السعر أثناء التحقق، فأعدنا الحساب. راجع الإجمالي ثم أكمل الدفع.', finishing: 'نحفظ بياناتك ونكمل الطلب…',
  },
  en: {
    title: 'Checkout', back: 'Back to cart', preview: 'Test environment — payment is simulated.',
    review: '1. Review order', delivery: '2. Delivery details', services: '3. Extra services (optional)', summary: 'Summary',
    notValid: 'Some cart lines need fixing — go back to the cart.',
    needAddress: 'Choose a delivery address, or complete the new one.', deliverTo: 'Deliver to',
    check: 'Continue checkout', checking: 'Confirming availability and calculating shipping…', checkSteps: ['1/3 Checking availability with the supplier…', '2/3 Calculating shipping to your address…', '3/3 Preparing your total, a moment…'], recheck: 'Continue checkout',
    goods: 'Goods', shipping: 'Shipping to Saudi Arabia', servicesLine: 'Extra services',
    fee: 'Service fee', vat: 'VAT', total: 'Total', importLine: 'Import VAT and customs clearance',
    customs: 'Includes 15% import VAT and customs clearance.',
    validUntil: (t) => `Price held until ${t}.`, expired: 'This price has expired — check again.',
    changed: 'The supplier price changed since you added some items:', changedLine: (n, a, b) => `${n}: from ${a} to ${b} per unit`,
    ackChange: 'I have seen the price change and accept the new price.',
    pay: 'Confirm order and pay', paying: 'Creating your order…', checkoutOff: 'Checkout is not enabled yet.',
    payOff: 'Payment is not enabled yet — you can place the order and pay later from its page.', placeOnly: 'Confirm order',
    blocked: 'This order cannot be completed right now:',
    fulfilNotice: (days) => `Fulfilment depends on product availability and shipping feasibility. If it cannot be fulfilled, you get back the full amount you paid${days ? `. Expected delivery: ${days} days.` : '.'}`,
    previewNote: 'This is your cart\'s price. We confirm it with availability now, then you pay.',
    noExtra: 'Note: you will not be asked to pay anything more on delivery.',
    needsShip: 'We could not calculate shipping just now. Please try again in a moment.', retryShip: 'Try again',
    reason: {
      parcel: 'This order is heavier than a single parcel to Saudi Arabia allows (10 kg). Reduce the quantity or split the products across two orders.',
      minimum: (m) => `The minimum order value is ${m}. Go back to the cart and add products.`,
      channel: 'Some products are not yet available for online payment.',
      supplier: 'The supplier did not confirm the price or availability of some products.',
      line: 'Some lines need fixing in the cart (quantity or availability).',
      config: 'Online payment is not available right now.',
      busy: 'The service is busy — try again in a minute.',
    },
    failed: 'Could not complete. Please try again.', loading: '…',
    signInToCheck: 'Sign in to calculate the final total.', signIn: 'Sign in',
    guestLimit: 'Too many total calculations from this device — sign in to continue, or try again shortly.', challenge: "We couldn't verify the request. Please try again.",
    complete: 'Confirm amount and continue', reviewLead: 'This is the final amount. Review it, then confirm to continue.', challengeDown: 'The verification service is busy. Please try again shortly.', notBuyer: "This account isn't a buyer account and can't buy from the market. Use a buyer account.",
    requoted: 'The price expired while you were verifying, so we recalculated it. Review the total, then complete payment.', finishing: 'Saving your details and completing the order…',
  },
  zh: {
    title: '结算', back: '返回购物车', preview: '测试环境 — 付款为模拟。',
    review: '1. 核对订单', delivery: '2. 收货信息', services: '3. 增值服务（可选）', summary: '摘要',
    notValid: '购物车中有需要修正的行 — 请返回购物车。',
    needAddress: '请选择收货地址，或填写完整的新地址。', deliverTo: '送至',
    check: '继续结账', checking: '正在确认库存并计算运费…', checkSteps: ['1/3 正在向供应商确认库存…', '2/3 正在计算到您地址的运费…', '3/3 正在生成总价，请稍候…'], recheck: '继续结账',
    goods: '货品', shipping: '运至沙特', servicesLine: '增值服务',
    fee: '服务费', vat: '增值税', total: '合计', importLine: '进口增值税及清关费',
    customs: '含15%进口增值税及清关费。',
    validUntil: (t) => `价格保留至 ${t}。`, expired: '价格已过期 — 请重新核实。',
    changed: '部分商品的供应商价格已变化：', changedLine: (n, a, b) => `${n}：单价由 ${a} 变为 ${b}`,
    ackChange: '我已知悉价格变化并接受新价格。',
    pay: '确认订单并付款', paying: '正在创建订单…', checkoutOff: '结算尚未开放。',
    payOff: '付款尚未开放 — 可先下单，稍后在订单页付款。', placeOnly: '确认订单',
    blocked: '此订单暂无法完成：',
    fulfilNotice: (days) => `订单能否履行取决于商品库存和运输可行性。如无法履行，将退还您支付的全部款项${days ? `；预计送达时间：${days} 天。` : '。'}`,
    previewNote: '这是购物车中的价格。我们现在确认库存，确认后付款。',
    noExtra: '注意：收货时不会再向您收取任何费用。',
    needsShip: '暂时无法计算运费，请稍后重试。', retryShip: '重试',
    reason: {
      parcel: '此订单超过运往沙特的单个包裹重量上限（10 公斤）。请减少数量或分成两个订单。',
      minimum: (m) => `最低订单金额为 ${m}。请返回购物车添加商品。`,
      channel: '部分商品暂不支持在线付款。',
      supplier: '供应商未确认部分商品的价格或库存。',
      line: '购物车中有需要修正的行（数量或库存）。',
      config: '在线付款暂不可用。',
      busy: '服务繁忙 — 请一分钟后重试。',
    },
    failed: '无法完成，请重试。', loading: '…',
    signInToCheck: '请登录以计算最终总价。', signIn: '登录',
    guestLimit: '此设备的计算请求过多——请登录后继续，或稍后再试。', challenge: '无法验证请求，请重试。',
    complete: '确认金额并继续', reviewLead: '这是最终金额。请核对后确认继续。', challengeDown: '验证服务繁忙，请稍后再试。', notBuyer: '此账户不是买家账户，无法在市场购买。请使用买家账户。',
    requoted: '验证期间价格已过期，我们已重新计算。请查看总价后完成付款。', finishing: '正在保存您的信息并完成订单…',
  },
};

/** Server block codes → the few things a buyer can understand and act on. */
export function blockGroups(codes = []) {
  const g = new Set();
  for (const c of codes) {
    if (c === 'channel_not_supported' || c === 'supplier_reference_missing') g.add('channel');
    else if (c.startsWith('preview_') || c === 'line_unavailable' || c === 'sku_mapping_missing' || c === 'domestic_shipping_unknown') g.add('supplier');
    else if (c.startsWith('line:')) g.add('line');
    else if (c === 'busy_try_again') g.add('busy');
    else if (c === 'over_parcel_limit') g.add('parcel');
    else if (c === 'below_minimum') g.add('minimum');
    else g.add('config');      // warehouse_unknown, …
  }
  return [...g];
}

/** A date `days` from today (an option's own transit days). */
function daysFromNow(days) { const d = new Date(); d.setDate(d.getDate() + Number(days || 0)); return d; }

export default function MarketCheckout({ lang = 'ar', user, exchangeRates }) {
  const nav = useNavigate();
  const isAr = lang === 'ar';
  const c = T[lang] || T.ar;
  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';
  usePageTitle('market', lang);
  // What the cart sent (point 2, owner 2026-11-02): one option with only its lines, or «اطلب سعر الشحن»
  // for lines no rate covers. Void (the whole cart) when the cart changed since.
  const [sel] = useState(() => getCheckoutSelection());
  const only = useMemo(() => (sel ? new Set(sel.keys) : null), [sel]);
  const { lines, priced, loading } = useCartPricing(exchangeRates, only);
  // The chosen, valid delivery address (a snapshot — saAddress.toDelivery), or null.
  const [delivery, setDelivery] = useState(null);
  const [tried, setTried] = useState(false);
  const [config, setConfig] = useState(null);
  const [services, setServices] = useState([]);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState('');
  // the check takes 5–15 s (HioBuy: availability ~4–7 s, then the shipping quote ~5–9 s) — say which step
  // it is on rather than one line for half a minute (store test 2026-11-03)
  const [checkSecs, setCheckSecs] = useState(0);
  useEffect(() => {
    if (busy !== 'check') { setCheckSecs(0); return undefined; }
    const t0 = Date.now();
    const t = setInterval(() => setCheckSecs(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(t);
  }, [busy]);
  const [err, setErr] = useState('');
  const [ack, setAck] = useState(false);
  const [now, setNow] = useState(Date.now());
  // What a guest typed and calculated, kept across signing in (checkoutState.js).
  const restored = useRef(loadCheckoutState());
  const [draft, setDraft] = useState(() => restored.current.draft || null);
  const [authOpen, setAuthOpen] = useState(false);
  const [preferId, setPreferId] = useState(null);
  const [needSignIn, setNeedSignIn] = useState(false);
  const finishing = useRef(false);
  // A second click in the same moment must not start a second payment
  // attempt: the busy state disables the button only from the next render.
  const paying = useRef(false);
  useEffect(() => { getCheckoutConfig().then((r) => setConfig(r.ok ? r.data : { checkout_enabled: false, payments_enabled: false, services: [] })); }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(t); }, []);
  useEffect(() => { if (!lines.length && !busy) nav('/market/cart', { replace: true }); }, [lines.length, nav, busy]);
  // Anything the buyer changes makes the last quote stale.
  const cartKey = useMemo(() => JSON.stringify(lines.map((l) => [l.product_id, l.sku_key, l.qty])), [lines]);
  const deliveryKey = JSON.stringify(delivery);
  const servicesKey = JSON.stringify(services);
  useEffect(() => {
    setQuote((q) => (q && q.keys && q.keys.cart === cartKey && q.keys.delivery === deliveryKey && q.keys.services === servicesKey ? q : null));
    setAck(false);
  }, [cartKey, deliveryKey, servicesKey]);
  // Back from a Google / Apple sign-in (or a reload): the guest's quote, if it
  // was calculated for exactly this cart and address and has not expired.
  useEffect(() => {
    const q = restored.current.quote;
    if (q && !quote && q.keys?.cart === cartKey && q.keys?.delivery === deliveryKey && new Date(q.expires_at).getTime() > Date.now()) setQuote(q);
  }, [cartKey, deliveryKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const deliveryOk = !!delivery;
  // Growth OS (step 3): which checkout step a buyer reached. The address is
  // never sent — only whether it was valid, the failing field's name, and the city.
  useEffect(() => { track('checkout_step_viewed', { step: 'address' }); }, []);
  const expired = quote && new Date(quote.expires_at).getTime() <= now;
  const ready = quote?.status === 'ready' && !expired;
  // The delivered-price model (owner, 2026-10-29): the quote carries three
  // options — air and sea (shipping and customs included) and «توصيل سريع»
  // (SA001G, shown as + over air). One method for the whole order.
  const dcfg = useDeliveredConfig();
  const [shipMethod, setShipMethod] = useState(() => getCartMethod());
  const opts = quote?.shipping_options || null;
  // the cart's own figures for the chosen option — shown until the quote confirms them
  const preview = sel && !sel.ask && sel.method && sel.preview && Number(sel.preview.total_sar) > 0 ? sel.preview : null;
  // the buyer's own pick for this cart, else the cheaper of air and sea
  const v2 = opts?.v === 2;   // the three-option cart (point 2)
  const method = !opts ? 'fast'
    // point 2: the option the cart chose, else the server's cheapest
    : v2 ? (['sea', 'air', 'fast'].find((m) => m === (sel?.method || shipMethod) && opts[m]?.available)
      || (opts.default === 'express' ? 'fast' : opts.default) || 'air')
    : shipMethod === 'sea' && opts.sea?.available ? 'sea'
    : shipMethod === 'fast' && opts.fast?.available ? 'fast'
    : shipMethod === 'air' ? 'air'
    // a cart with a product air does not suit (rule B): sea by default
    : opts.sea_first && opts.sea?.available ? 'sea' : cheaperMethod(opts);
  const pickMethod = (m) => { if (m !== method) track('cart_shipping_changed', { from_option: method, to_option: m }); setShipMethod(m); setCartMethod(m); };
  const view = opts && (method !== 'fast' || v2) && opts[method]
    ? { goods_sar: quote.goods_sar, shipping_sar: opts[method].shipping_sar, services_sar: opts[method].services_sar, import_sar: opts[method].import_sar || 0,
        service_fee_sar: opts[method].fee_sar, vat_sar: opts[method].vat_sar, total_sar: opts[method].total_sar, delivered: true,
        days: v2 && opts[method].days_min != null ? `${opts[method].days_min}–${opts[method].days_max}` : null }
    : quote;
  // every price on this page is a product price (owner, 2026-11-02: the fee, the allowance and the rounding
  // always apply): at the cards' rate before the quote, at the quote's own rate after it
  const pcfg = dcfg ? { ...dcfg, ...(v2 && opts.fee_rate != null ? { fee_rate: opts.fee_rate } : {}) } : null;
  const unitShown = (r) => (pcfg && r.quote?.unitSar != null ? productPrice(r.quote.unitSar, pcfg) : null);
  const lineShown = (r) => (unitShown(r) != null ? Math.round(unitShown(r) * r.line.qty * 100) / 100 : null);
  const productsBefore = priced ? Math.round(priced.groups.reduce((n, g) => n + g.lines.reduce((k, r) => k + (r.valid ? lineShown(r) || 0 : 0), 0), 0) * 100) / 100 : null;
  // never a fee line (owner, 2026-11-02): the products carry the fee, «الشحن والجمارك» carries import charges
  const productsSar = view ? Math.round((Number(view.goods_sar) + Number(view.service_fee_sar || 0)) * 100) / 100 : null;
  const shipCustomsSar = view ? Math.round((Number(view.shipping_sar || 0) + Number(view.import_sar || 0)) * 100) / 100 : null;
  const dt = DELIVERED_T[lang] || DELIVERED_T.ar;
  const needsShip = quote?.status === 'needs_shipping_quote' && !expired;
  // Rule C (owner, 2026-10-30/31): the cart waits for staff to confirm its shipping price — one tap, no payment
  // a server from before the unification marks a staff-priced cart only with staff_shipping (sent as 'fast')
  const confirmQuote = needsShip && (quote?.shipping_options?.confirm?.available === true || quote?.staff_shipping === true);
  const needsAck = ready && quote.price_changes?.length > 0;

  const check = async () => {
    setErr(''); setBusy('check'); setTried(true);
    track('address_submitted', deliveryOk
      ? { valid: true, city: delivery.city || undefined }
      : { valid: false, field_with_error: Object.keys(validateAddress(draft || {}))[0], city: draft?.city || undefined });
    if (!deliveryOk) { setBusy(''); setErr(c.needAddress); return; }
    if (!user && !config?.guest_checkout) { setBusy(''); setNeedSignIn(true); setErr(c.signInToCheck); return; }
    const body = { lines: lines.map((l) => ({ product_id: l.product_id, sku_key: l.sku_key, qty: l.qty })), delivery, services, lang,
      ...(sel?.ask ? { ask: true, askMethod: sel.method } : {}) };
    // A guest's total is calculated only after Cloudflare Turnstile vouches for
    // the visitor — verified on the server; a token is single-use, so a refused
    // one is replaced once automatically before the buyer sees anything.
    const ask = async () => {
      let turnstileToken;
      if (!user && turnstileEnabled) {
        try { turnstileToken = await getTurnstileToken(); } catch (e) {
          console.warn('[MarketCheckout] turnstile:', e?.message || e);
          return { ok: false, error: 'challenge_failed' };
        }
      }
      return requestQuote({ ...body, turnstileToken });
    };
    let r = await ask();
    if (!r.ok && !user && turnstileEnabled && r.error === 'challenge_failed') r = await ask();
    setBusy('');
    if (!r.ok) {
      // The server's reason, in words the buyer can act on — never one generic line for all.
      const why = {
        marketplace_not_public: c.reason.config, sign_in_required: c.signInToCheck, not_signed_in: c.signInToCheck, unauthorized: c.signInToCheck,
        guest_rate_limited: c.guestLimit, challenge_failed: c.challenge, challenge_unavailable: c.challengeDown, invalid_delivery: c.needAddress, service_not_offered: c.failed,
      }[r.error];
      if (['sign_in_required', 'not_signed_in', 'unauthorized', 'guest_rate_limited'].includes(r.error)) setNeedSignIn(true);
      if (!why) console.warn('[MarketCheckout] quote refused:', r.httpStatus || '', r.error || '');
      track('checkout_step_viewed', { step: `quote_error:${r.error || 'unknown'}` });
      setErr(why || c.failed);
      return;
    }
    const q = { ...r.quote, guest_token: r.guest_token || null, keys: { cart: cartKey, delivery: deliveryKey, services: servicesKey } };
    setQuote(q); setAck(false); setNeedSignIn(false);
    track('checkout_step_viewed', { step: 'review' });
    if (q.guest_token) saveCheckoutState({ quote: q });
  };

  // After sign-in: the guest's quote becomes this account's, the address and
  // name are saved, and the order goes on to payment. Runs once per session.
  const finishAfterSignIn = useCallback(async () => {
    if (finishing.current || !user || !quote) return;
    finishing.current = true;
    setErr(''); setBusy('finish'); setAuthOpen(false);
    try {
      const { data: prof } = await sb.from('profiles').select('role, full_name, phone').eq('id', user.id).maybeSingle();
      if (prof && String(prof.role || 'buyer').toLowerCase() !== 'buyer') { setErr(c.notBuyer); setBusy(''); return; }
      let q = quote;
      if (q.guest_token) {
        const cl = await claimQuote(q.id, q.guest_token);
        if (!cl.ok) {
          // Expired or gone: recalculate as this account and let the buyer see it before paying.
          console.warn('[MarketCheckout] claim refused:', cl.error || '');
          saveCheckoutState({ quote: null, resume: false });
          restored.current = { ...restored.current, resume: false };
          setQuote(null); setBusy('');
          await check();
          setErr(c.requoted);
          return;
        }
        q = { ...q, guest_token: null };
        setQuote(q);
      }
      // Save what the guest typed: the address (once) and the name if the profile has none.
      const typed = restored.current.draft || draft;
      if (typed && !Object.keys(validateAddress(typed)).length) {
        const { rows } = await listAddresses();
        const same = rows.find((a) => a.shortAddress === (typed.shortAddress || '').toUpperCase() && String(a.buildingNo) === String(typed.buildingNo));
        if (same) setPreferId(same.id);
        else {
          const sv = await saveAddress({ ...typed, id: undefined }, user.id, { makeDefault: !rows.length });
          if (sv.error) console.warn('[MarketCheckout] address not saved:', sv.error);
          else setPreferId(sv.row.id);
        }
      }
      // Name and contact number on the profile, only where it has none. The
      // number is CONTACT data — it never becomes a sign-in method here (that
      // takes a code: phone-link, or the WhatsApp sign-in itself).
      const fill = {};
      if (prof && !prof.full_name && delivery?.name) fill.full_name = delivery.name;
      if (prof && !prof.phone && delivery?.phone) fill.phone = delivery.phone;
      if (Object.keys(fill).length) {
        const { error } = await sb.from('profiles').update(fill).eq('id', user.id);
        if (error) console.warn('[MarketCheckout] profile not updated:', error.message);
      }
      saveCheckoutState({ quote: null, resume: false, draft: null });
      restored.current = { ...restored.current, resume: false };
      setBusy('');
      await placeAndPay(q);
    } finally {
      finishing.current = false;
    }
  }, [user, quote, draft, delivery]); // eslint-disable-line react-hooks/exhaustive-deps

  // A session appeared while "Complete purchase" was waiting for it (a code
  // typed here, or back from Google/Apple): carry on to payment. Signing in
  // remounts this page, so the signal lives in checkoutState, not in memory —
  // and only for 30 minutes after the buyer pressed "Complete purchase".
  useEffect(() => {
    const r = restored.current;
    const resume = authOpen || (r.resume === true && Date.now() - Number(r.resumeAt || 0) < 30 * 60 * 1000);
    if (user && resume && quote && (quote.status === 'ready' || quote?.shipping_options?.confirm?.available === true || quote?.staff_shipping === true) && delivery) finishAfterSignIn();
  }, [user, authOpen, quote, delivery]); // eslint-disable-line react-hooks/exhaustive-deps

  const placeAndPay = async (q = quote) => {
    if (!user) {
      // A guest: verify in place, keep everything for the way back.
      saveCheckoutState({ quote: q, draft, resume: true, resumeAt: Date.now() });
      track('checkout_step_viewed', { step: 'signin' });
      setAuthOpen(true);
      return;
    }
    if (paying.current) return;
    paying.current = true;
    setErr(''); setBusy('pay');
    // «اطلب تثبيت سعر الشحن»: one request, no payment (market_create_order 'confirm')
    const o = await createOrder(q.id, q.shipping_options?.confirm?.available ? 'confirm' : q.shipping_options ? method : 'fast');
    if (!o.ok) {
      track('checkout_step_viewed', { step: /quote_expired/.test(o.error) ? 'order_error:quote_expired' : 'order_error' });
      paying.current = false; setBusy(''); setErr(/quote_expired/.test(o.error) ? c.expired : c.failed); return;
    }
    // only the lines this order took leave the cart (point 2: the rest wait for another option or a price)
    if (sel) removeLines(sel.keys); else clearCart();
    clearCheckoutState();
    const orderId = o.data.order_id;
    recordOrderAttribution(orderId, getAnonId());   // fire-and-forget: never delays payment
    if (o.data.status === 'awaiting_shipping_quote' || !config?.payments_enabled) { nav(`/market/orders/${orderId}?placed=1`); return; }
    const back = `${window.location.origin}/market/orders/${orderId}`;
    const p = await startPayment(orderId, back, lang);
    if (p.ok && p.url) {
      track('payment_started', { order_id: orderId, amount_sar: view?.total_sar, gateway: 'telr' });
      try { sessionStorage.setItem(`maabar.growth.pay.${orderId}`, String(Date.now())); } catch { /* timing only */ }
      window.location.assign(p.url);
      return;
    }
    track('checkout_step_viewed', { step: 'payment_start_error' });
    nav(`/market/orders/${orderId}`);   // the order exists; payment can be retried from its page
  };

  const section = { border: '1px solid var(--border)', borderRadius: 16, padding: 16, background: 'var(--surface-raised)', marginBottom: 14 };
  const h2 = { fontSize: 15.5, margin: '0 0 12px', fontFamily: font };
  const btn = (on) => ({ width: '100%', padding: '12px 0', borderRadius: 12, border: 'none', fontSize: 14, fontWeight: 600, fontFamily: font,
    background: on ? '#1A1814' : 'var(--border)', color: on ? '#fff' : 'var(--text-muted)', cursor: on ? 'pointer' : 'not-allowed' });
  const title = (l) => (isAr ? l.title_ar : lang === 'zh' ? l.title_zh : l.title_en) || l.title_en || l.title_ar || '—';
  const timeText = (iso) => new Date(iso).toLocaleTimeString(isAr ? 'ar-SA' : lang === 'zh' ? 'zh-CN' : 'en-GB', { hour: '2-digit', minute: '2-digit' });

  return (
    <MarketShell lang={lang} user={user}>
      {/* Clarity: the whole checkout is masked — names, phones, national address. */}
      <div className="fx-wrap" data-clarity-mask="True">
        {MARKET_API_IS_LOCAL && <PreviewBanner text={c.preview} isAr={isAr} />}
        <button className={`fx-back${isAr ? ' ar' : ''}`} onClick={() => nav('/market/cart')}>{isAr ? '→ ' : '← '}{c.back}</button>
        <h1 className={`fx-h1${isAr ? ' ar' : ''}`} style={{ fontSize: isAr ? 24 : 27, margin: '6px 0 18px' }}>{c.title}</h1>
        {loading || !priced || !config ? <p style={{ color: 'var(--text-secondary)' }}>{c.loading}</p> : (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start', fontFamily: font }}>
            <div style={{ flex: '1 1 560px', minWidth: 0 }}>
              <section style={section} data-checkout-review="1">
                <h2 style={h2}>{c.review}</h2>
                {!priced.valid && <p style={{ fontSize: 12.5, margin: '0 0 10px', color: '#B3261E' }}>{c.notValid}</p>}
                {priced.groups.flatMap((g) => g.lines.map((r) => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ color: 'var(--text-primary)' }}>{g.product ? marketTitle(g.product, lang) : '—'}</span>
                      <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: 12 }}>{variantText(g.product, r.sku, lang)}{variantText(g.product, r.sku, lang) ? ' · ' : ''}<span dir="ltr">{r.line.qty.toLocaleString('en-US')} × {formatSar(unitShown(r)) || '—'}</span></span>
                    </span>
                    <strong style={{ whiteSpace: 'nowrap' }}>{r.valid ? formatSar(lineShown(r)) || '—' : '—'}</strong>
                  </div>
                )))}
              </section>

              <section style={section} data-checkout-delivery="1">
                <h2 style={h2}>{c.delivery}</h2>
                <AddressBook user={user} lang={lang} onSelect={setDelivery} font={font} showErrors={tried}
                  draft={draft} preferId={preferId} onDraftChange={(d) => { setDraft(d); saveCheckoutState({ draft: d }); }} />
              </section>

              {config.services?.length > 0 && (
                <section style={section} data-checkout-services="1">
                  <h2 style={h2}>{c.services}</h2>
                  {config.services.map((s) => (
                    <label key={s.code} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 0', fontSize: 13.5, cursor: 'pointer' }}>
                      <input type="checkbox" data-service={s.code} checked={services.includes(s.code)}
                        onChange={(e) => setServices(e.target.checked ? [...services, s.code] : services.filter((x) => x !== s.code))} />
                      <span style={{ flex: 1 }}>
                        <strong>{s[`name_${lang}`] || s.name_en || s.name_ar}</strong>
                        {(s[`description_${lang}`] || s.description_en) && <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 12 }}>{s[`description_${lang}`] || s.description_en}</span>}
                      </span>
                      <span>{formatSar(Number(s.price_sar))}</span>
                    </label>
                  ))}
                </section>
              )}
            </div>

            <aside style={{ flex: '0 1 360px', minWidth: 260 }}>
              <section style={section} data-checkout-summary="1">
                <h2 style={h2}>{c.summary}</h2>
                {!ready && !needsShip && (
                  <>
                    {/* the option and total the cart showed, at once (store test 2026-11-03) — confirmed by the quote */}
                    {preview ? (
                      <div data-checkout-preview={sel.method}>
                        <p style={{ margin: '0 0 6px', fontSize: 13.5, fontWeight: 600 }}>{sel.method === 'air' ? dt.fast : sel.method === 'sea' ? dt.cheap : dt.express}
                          {preview.days_min != null && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 400 }}>{dt.arrives(shortDate(daysFromNow(preview.days_min), lang), shortDate(daysFromNow(preview.days_max), lang))}</span>}</p>
                        <Row k={dt.productsTotal} v={<span data-checkout-preview-products={preview.products_sar}>{formatSar(preview.products_sar)}</span>} />
                        <Row k={dt.shipCustoms} v={<span data-checkout-preview-shipping={preview.shipping_sar}>{formatSar(preview.shipping_sar)}</span>} />
                        <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 6 }}>
                          <Row k={<strong>{c.total}</strong>} v={<strong data-checkout-preview-total={preview.total_sar} style={{ fontSize: 16 }}>{formatSar(preview.total_sar)}</strong>} />
                        </div>
                        <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '4px 0 0' }}>{c.previewNote}</p>
                      </div>
                    ) : (
                    <Row k={dt.productsTotal} v={<strong data-checkout-goods={productsBefore ?? ''}>{formatSar(productsBefore) || '—'}</strong>} />
                    )}
                    <button type="button" data-checkout-check="1" disabled={!priced.valid || busy === 'check'} onClick={check} style={{ ...btn(priced.valid && busy !== 'check'), marginTop: 10 }}>
                      {busy === 'check' ? (checkSecs < 5 ? c.checkSteps[0] : checkSecs < 11 ? c.checkSteps[1] : c.checkSteps[2]) : c.check}
                    </button>
                  </>
                )}
                {quote && quote.status === 'blocked' && (
                  <div data-checkout-blocked={quote.block_reasons.join(',')} style={{ marginTop: 12, padding: 12, borderRadius: 12, background: '#FFF4F2', border: '1px solid #F2C8C0', fontSize: 12.5, color: '#8A2A1C', lineHeight: 1.7 }}>
                    <strong>{c.blocked}</strong>
                    <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>{blockGroups(quote.block_reasons).map((k) => <li key={k} data-block-group={k}>{typeof c.reason[k] === 'function' ? c.reason[k](formatSar(quote.min_cart_sar)) : c.reason[k]}</li>)}</ul>
                  </div>
                )}
                {expired && <p data-quote-expired="1" style={{ color: '#B3261E', fontSize: 12.5 }}>{c.expired}</p>}
                {ready && delivery && (
                  <div data-review-address="1" style={{ fontSize: 12.5, margin: '0 0 10px', padding: '8px 10px', borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: 11.5, marginBottom: 2 }}>{c.deliverTo}</div>
                    <AddressSummary a={delivery} lang={lang} />
                  </div>
                )}
                {ready && (
                  <div data-quote-ready={quote.id} data-q-method={method}>
                    {opts && (
                      <div data-q-methods="1" style={{ display: 'grid', gap: 8, margin: '0 0 10px' }}>
                        {(v2 ? ['sea', 'air', 'fast'] : opts.sea_first ? ['sea', 'fast', 'air'] : ['air', 'sea', 'fast']).map((m) => {
                          const o = opts[m]; const on = method === m;
                          const avail = !!o?.available && o.total_sar != null;
                          const under = !!o?.below_minimum;
                          const arr = v2 ? (o?.days_min != null ? { from: daysFromNow(o.days_min), to: daysFromNow(o.days_max) } : null) : m !== 'fast' && dcfg ? arrival(m, dcfg) : null;
                          const extra = !v2 && m === 'fast' && avail && opts.air?.total_sar != null ? Math.round((o.total_sar - opts.air.total_sar) * 100) / 100 : null;
                          const save = m === 'sea' && avail && opts.air?.total_sar != null ? Math.round((opts.air.total_sar - o.total_sar) * 100) / 100 : 0;
                          return (
                            <button key={m} type="button" data-q-method-option={m} data-q-method-on={on ? '1' : '0'} disabled={!avail} onClick={() => pickMethod(m)}
                              style={{ textAlign: 'start', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', padding: m === 'fast' ? '7px 12px' : '10px 12px',
                                borderRadius: 12, border: m === 'fast' ? '1px dashed' : '1.5px solid', borderColor: on ? '#1A1814' : 'var(--border)',
                                background: avail ? 'var(--surface)' : 'var(--bg-hero)', opacity: avail ? 1 : 0.55, cursor: avail ? 'pointer' : 'not-allowed', fontFamily: font }}>
                              <span>
                                <span style={{ fontSize: m === 'fast' ? 12.5 : 13.5, fontWeight: 600 }}>{m === 'air' ? dt.fast : m === 'sea' ? dt.cheap : dt.express}</span>
                                {on && <span data-q-method-name={m} style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', fontWeight: 600 }}>{(METHOD_NAME[lang] || METHOD_NAME.ar)[m]}</span>}
                                {under && <span data-q-below-min={m} style={{ display: 'block', fontSize: 11.5, color: '#7A5A22' }}>{c.reason.minimum(formatSar(quote.min_cart_sar))}</span>}
                                {arr && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)' }}>{dt.arrives(shortDate(arr.from, lang), shortDate(arr.to, lang))}</span>}
                                {save > 0 && <span data-q-sea-save={save} style={{ display: 'block', fontSize: 11.5, color: '#2E7D32', fontWeight: 600 }}>{dt.save(formatSar(save))}</span>}
                                {/* never a bare «—» (store test 2026-11-03): what this option covers, or why it asks */}
                                {v2 && avail && o.line_count > 0 && o.lines?.length < o.line_count && <span data-q-covers={`${o.lines.length}/${o.line_count}`} style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)' }}>{dt.covers(o.lines.length, o.line_count)}</span>}
                                {v2 && !avail && !under && <span data-q-ask-why={m} style={{ display: 'block', fontSize: 11.5, color: '#7A5A22' }}>{o?.pending ? dt.pricing : [...new Set((o?.ask_reasons || []).map((k) => dt.askWhy[k] || dt.askWhy.none))].join('، ') || dt.askWhy.none}</span>}
                              </span>
                              <strong data-q-method-total={o?.total_sar ?? ''} style={{ fontSize: m === 'fast' ? 13 : 14.5 }}>
                                {!avail ? (v2 ? <span style={{ fontSize: 12, fontWeight: 600, color: '#7A5A22' }}>{dt.confirmAsk}</span> : '—') : m === 'fast' && extra != null ? `${extra >= 0 ? '+' : '−'}${formatSar(Math.abs(extra))}` : formatSar(o.total_sar)}
                              </strong>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {/* products (the fee inside, never its own line) and shipping & customs (owner, 2026-11-02) */}
                    <Row k={dt.productsTotal} v={<span data-q-goods={productsSar}>{formatSar(productsSar)}</span>} />
                    <Row k={dt.shipCustoms} v={<span data-q-shipping={shipCustomsSar}>{formatSar(shipCustomsSar)}</span>} />
                    {view.services_sar > 0 && <Row k={c.servicesLine} v={formatSar(view.services_sar)} />}
                    {view.vat_sar > 0 && <Row k={c.vat} v={formatSar(view.vat_sar)} />}
                    <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 6 }}>
                      <Row k={<strong>{c.total}</strong>} v={<strong data-q-total={view.total_sar} style={{ fontSize: 16 }}>{formatSar(view.total_sar)}</strong>} />
                    </div>
                    {!view.delivered && <p data-customs-note="1" style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '6px 0 0', lineHeight: 1.6 }}>{c.customs}</p>}
                    <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '4px 0 0' }}>{c.validUntil(timeText(quote.expires_at))}</p>
                    {needsAck && (
                      <div data-price-changes={quote.price_changes.length} style={{ marginTop: 10, padding: 10, borderRadius: 10, background: '#FFF8E6', border: '1px solid rgba(154,118,54,0.35)', fontSize: 12.5, color: '#7A5A22' }}>
                        <strong>{c.changed}</strong>
                        <ul style={{ margin: '4px 0 6px', paddingInlineStart: 18 }}>
                          {quote.price_changes.map((pc) => <li key={pc.index}>{c.changedLine(title(quote.lines[pc.index]), formatSar(pc.before_unit_sar), formatSar(pc.after_unit_sar))}</li>)}
                        </ul>
                        <label style={{ display: 'flex', gap: 8, cursor: 'pointer' }}><input type="checkbox" data-ack-change="1" checked={ack} onChange={(e) => setAck(e.target.checked)} />{c.ackChange}</label>
                      </div>
                    )}
                    <p data-fulfil-notice="1" style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 0', lineHeight: 1.7, padding: '8px 10px', borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)' }}>{c.fulfilNotice(view.days ? view.days : view.delivered && dcfg && method !== 'fast' ? `${dcfg[`${method}_days_min`]}–${dcfg[`${method}_days_max`]}` : quote.transit?.min_days != null ? `${quote.transit.min_days}–${quote.transit.max_days}` : null)}</p>
                    {/* One paragraph and a note (owner, 2026-10-26) — they replace the separate transit line and «priceTerms». */}
                    <p data-no-extra="1" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', margin: '6px 0 0' }}>{c.noExtra}</p>
                    <p data-review-lead="1" style={{ fontSize: 12.5, fontWeight: 600, margin: '10px 0 0' }}>{c.reviewLead}</p>
                    {!config.checkout_enabled ? (
                      <button type="button" disabled style={{ ...btn(false), marginTop: 12 }}>{c.checkoutOff}</button>
                    ) : (
                      <>
                        {!authOpen && (
                          <button type="button" data-checkout-pay="1" disabled={busy === 'pay' || busy === 'finish' || (needsAck && !ack)} onClick={() => placeAndPay()} style={{ ...btn(busy !== 'pay' && busy !== 'finish' && (!needsAck || ack)), marginTop: 12 }}>
                            {busy === 'pay' ? c.paying : busy === 'finish' ? c.finishing : !user ? c.complete : config.payments_enabled ? c.pay : c.placeOnly}
                          </button>
                        )}
                        {authOpen && !user && <CheckoutAuth delivery={delivery} lang={lang} font={font} onCancel={() => setAuthOpen(false)} />}
                        {!config.payments_enabled && <p style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{c.payOff}</p>}
                      </>
                    )}
                  </div>
                )}
                {confirmQuote && (
                  <div data-quote-confirm={quote.id} data-quote-staff-shipping={quote.staff_shipping ? quote.id : undefined}>
                    <p style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.7, margin: '4px 0 0' }}>{dt.confirmLine(dcfg?.confirm_hours ?? 48)}</p>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, margin: '2px 0 0' }}>{dt.confirmHow(dcfg?.confirm_hours ?? 48)}</p>
                    <button type="button" data-confirm-request="1" onClick={() => placeAndPay()} disabled={busy === 'pay'} style={{ ...btn(busy !== 'pay'), marginTop: 10 }}>
                      {busy === 'pay' ? '…' : dt.confirmAsk}</button>
                  </div>
                )}
                {needsShip && !confirmQuote && (
                  <div data-quote-needs-shipping={quote.id}>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, margin: '4px 0 0' }}>{c.needsShip}</p>
                    <button type="button" data-retry-shipping="1" onClick={check} disabled={busy === 'check'} style={{ ...btn(busy !== 'check'), marginTop: 10 }}>{busy === 'check' ? c.checking : c.retryShip}</button>
                  </div>
                )}
                {err && !(err === c.needAddress && deliveryOk) && <p data-checkout-error="1" style={{ color: '#B3261E', fontSize: 12.5 }}>{err}</p>}
                {needSignIn && !user && (
                  <button type="button" data-checkout-signin="1" onClick={() => { saveCheckoutState({ draft }); nav('/login?next=' + encodeURIComponent('/market/checkout')); }}
                    style={{ ...btn(true), marginTop: 6 }}>{c.signIn}</button>
                )}
              </section>
            </aside>
          </div>
        )}
      </div>
    </MarketShell>
  );
}
